import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, CheckCircle2, XCircle, MessageSquare, Sparkles, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/lib/supabase';

interface StepExecutionPanelProps {
  instance: {
    id: string;
    title: string;
    template_name: string;
    current_step: number;
    status: string;
    due_date: string | null;
    step_count: number;
    completed_steps: number;
  };
  onClose: () => void;
  onUpdated: () => void;
}

export function StepExecutionPanel({ instance, onClose, onUpdated }: StepExecutionPanelProps) {
  const [comment, setComment] = useState('');
  const queryClient = useQueryClient();

  const { data: stepDetail, isLoading: stepLoading } = useQuery({
    queryKey: ['workflow-step', instance.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('workflow_step')
        .select('id, name, description, status, completed_at')
        .eq('instance_id', instance.id)
        .eq('status', 'active')
        .single();
      if (error) throw error;
      return data as { id: string; name: string; description: string; status: string; completed_at: string | null };
    },
  });

  const { data: audit } = useQuery({
    queryKey: ['workflow-audit', instance.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('workflow_audit')
        .select('id, action, actor_name, comment, created_at')
        .eq('instance_id', instance.id)
        .order('created_at', { ascending: false })
        .limit(20);
      if (error) throw error;
      return data as { id: string; action: string; actor_name: string; comment: string | null; created_at: string }[];
    },
  });

  const { data: suggestions } = useQuery({
    queryKey: ['workflow-ai', instance.id],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('workflow-ai', {
        body: { instance_id: instance.id },
      });
      if (error) return [];
      return (data?.suggestions as string[]) ?? [];
    },
  });

  const stepAction = useMutation({
    mutationFn: async (action: 'complete' | 'reject' | 'comment') => {
      const { error } = await supabase.functions.invoke('workflow-step-action', {
        body: { instance_id: instance.id, action, comment: comment || undefined },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-instances'] });
      queryClient.invalidateQueries({ queryKey: ['workflow-step', instance.id] });
      queryClient.invalidateQueries({ queryKey: ['workflow-audit', instance.id] });
      setComment('');
      onUpdated();
    },
  });

  return (
    <div className="w-[400px] shrink-0 space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base">{instance.title}</CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {stepLoading && <Skeleton className="h-20 w-full" />}
          {stepDetail && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Badge variant="outline">Current Step</Badge>
                <span className="text-sm font-medium">{stepDetail.name}</span>
              </div>
              <p className="text-sm text-muted-foreground">{stepDetail.description}</p>
            </div>
          )}
          <Separator />
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Add a comment (required for reject)..."
            rows={2}
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={() => stepAction.mutate('complete')} disabled={stepAction.isPending}>
              <CheckCircle2 className="mr-1 h-4 w-4" /> Complete Step
            </Button>
            <Button size="sm" variant="destructive" onClick={() => stepAction.mutate('reject')} disabled={stepAction.isPending || !comment}>
              <XCircle className="mr-1 h-4 w-4" /> Reject
            </Button>
            <Button size="sm" variant="outline" onClick={() => stepAction.mutate('comment')} disabled={stepAction.isPending || !comment}>
              <MessageSquare className="mr-1 h-4 w-4" /> Comment
            </Button>
          </div>
        </CardContent>
      </Card>

      {suggestions && suggestions.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Sparkles className="h-4 w-4 text-amber-500" /> AI Suggestions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {suggestions.map((s, i) => (
                <li key={i} className="flex gap-2 text-sm">
                  <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                  <span className="text-muted-foreground">{s}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Clock className="h-4 w-4" /> Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          {audit && audit.length > 0 ? (
            <div className="space-y-3">
              {audit.map((entry) => (
                <div key={entry.id} className="flex gap-3 text-sm">
                  <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary" />
                  <div>
                    <p>
                      <span className="font-medium">{entry.actor_name}</span>{' '}
                      <span className="text-muted-foreground">{entry.action}</span>
                    </p>
                    {entry.comment && <p className="mt-0.5 text-muted-foreground">"{entry.comment}"</p>}
                    <p className="text-xs text-muted-foreground">{new Date(entry.created_at).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No activity yet.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
